import React, { useState, useEffect, useCallback } from "react";
import { Sparkles, Instagram, Layers, X, Copy as CopyIcon } from "lucide-react";
import api from "../api";
import { T, primaryBtn } from "../theme";
import { VikiBadge } from "./shared";

export function VikiCaptionButton({ ideaText, profileId, savedCaption, onGenerated }) {
  const [state, setState] = useState(savedCaption ? "done" : "idle");
  const [caption, setCaption] = useState(savedCaption || "");

  async function generate() {
    setState("loading");
    try {
      const { data } = await api.post(`/profiles/${profileId}/caption`, { idea_text: ideaText });
      setCaption(data.caption);
      setState("done");
      if (onGenerated) onGenerated(data.caption);
    } catch (e) {
      setState("error");
    }
  }

  if (state === "idle") {
    return (
      <button data-testid="viki-caption-btn" onClick={generate} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.viki}66`, color: T.viki, borderRadius: 6, padding: "5px 10px", fontSize: 11, cursor: "pointer" }}>
        <Sparkles size={11} /> Genera didascalia con V.I.K.I.
      </button>
    );
  }
  if (state === "loading") return <div style={{ fontSize: 12, color: T.textDim }}>V.I.K.I. sta scrivendo…</div>;
  if (state === "error") {
    return <div style={{ fontSize: 12, color: T.rose }}>Non sono riuscita a generare la didascalia. <button onClick={generate} style={{ background: "none", border: "none", color: T.viki, cursor: "pointer", padding: 0, fontSize: 12 }}>Riprova</button></div>;
  }
  return (
    <div data-testid="viki-caption-result" style={{ marginTop: 6, background: T.surfaceAlt, border: `1px solid ${T.line}`, borderRadius: 8, padding: 10 }}>
      <div style={{ fontSize: 12, whiteSpace: "pre-wrap", marginBottom: 8, lineHeight: 1.5 }}>{caption}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => navigator.clipboard.writeText(caption)} style={{ fontSize: 11, background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 5, padding: "4px 8px", cursor: "pointer" }}>Copia</button>
        <button onClick={generate} style={{ fontSize: 11, background: "none", border: "none", color: T.textDim, cursor: "pointer", padding: "4px 4px" }}>Rigenera</button>
      </div>
    </div>
  );
}

const FORMAT_LABELS = { reel: "Script Reel", carosello: "Carosello (5 slide)", storia: "Storia" };

export function VikiMultiFormatButton({ ideaText, profileId }) {
  const [state, setState] = useState("idle");
  const [result, setResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  async function generate() {
    setState("loading");
    try {
      const { data } = await api.post(`/profiles/${profileId}/multiformat`, { idea_text: ideaText });
      setResult(data);
      setState("done");
    } catch (e) {
      setState("error");
    }
  }

  function copy(key) {
    navigator.clipboard.writeText(result[key]);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  }

  if (state === "idle") {
    return (
      <button data-testid="viki-multiformat-btn" onClick={generate} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.viki}66`, color: T.viki, borderRadius: 6, padding: "5px 10px", fontSize: 11, cursor: "pointer" }}>
        <Layers size={11} /> Reel + Carosello + Storia
      </button>
    );
  }
  if (state === "loading") return <div style={{ fontSize: 12, color: T.textDim }}>V.I.K.I. sta preparando le tre versioni…</div>;
  if (state === "error") {
    return <div style={{ fontSize: 12, color: T.rose }}>Non sono riuscita a generare le tre versioni. <button onClick={generate} style={{ background: "none", border: "none", color: T.viki, cursor: "pointer", padding: 0, fontSize: 12 }}>Riprova</button></div>;
  }
  return (
    <div data-testid="viki-multiformat-result" style={{ marginTop: 6, background: T.surfaceAlt, border: `1px solid ${T.viki}44`, borderRadius: 8, padding: 10, position: "relative" }}>
      <button data-testid="viki-multiformat-close" onClick={() => { setState("idle"); setResult(null); }} title="Chiudi" style={{ position: "absolute", top: 6, right: 6, background: "transparent", border: "none", color: T.textDim, cursor: "pointer", padding: 2 }}><X size={13} /></button>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {["reel", "carosello", "storia"].map((key) => (
          <div key={key} data-testid={`multiformat-${key}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontFamily: T.fontHead, fontSize: 11, color: T.viki }}>{FORMAT_LABELS[key]}</span>
              <button data-testid={`multiformat-copy-${key}`} onClick={() => copy(key)} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 4, padding: "2px 6px", cursor: "pointer" }}>
                <CopyIcon size={9} /> {copiedKey === key ? "Copiato!" : "Copia"}
              </button>
            </div>
            <div style={{ fontSize: 12, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{result[key]}</div>
          </div>
        ))}
      </div>
      <button onClick={generate} style={{ marginTop: 10, fontSize: 11, background: "none", border: "none", color: T.textDim, cursor: "pointer", padding: 0 }}>Rigenera tutte</button>
    </div>
  );
}

export function VikiStrategyPanel({ profileId }) {  const [state, setState] = useState("idle");
  const [strategy, setStrategy] = useState("");

  async function generate() {
    setState("loading");
    try {
      const { data } = await api.post(`/profiles/${profileId}/strategy`);
      setStrategy(data.strategy);
      setState("done");
    } catch (e) {
      setState("error");
    }
  }

  return (
    <div data-testid="viki-strategy-panel" style={{ background: `linear-gradient(150deg, ${T.surface}, #F6F4FF)`, border: `1px solid ${T.viki}33`, borderRadius: 22, padding: 22, marginBottom: 20, boxShadow: "0 8px 28px rgba(124,77,255,0.16)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <VikiBadge />
        <div style={{ fontFamily: T.fontHead, fontSize: 15 }}>Strategia su misura per le prossime due settimane</div>
      </div>
      {state === "idle" && (
        <>
          <div style={{ fontSize: 13, color: T.textDim, marginBottom: 14, maxWidth: 560 }}>
            Non un elenco generico: V.I.K.I. guarda i dati reali di questo profilo — follower, andamento,
            cliente ideale, eventuale concorrente — e scrive una strategia su misura, in linguaggio semplice.
          </div>
          <button data-testid="viki-strategy-generate-btn" onClick={generate} style={{ ...primaryBtn, background: T.viki, display: "flex", alignItems: "center", gap: 6 }}>
            <Sparkles size={14} /> Genera la mia strategia
          </button>
        </>
      )}
      {state === "loading" && <div style={{ fontSize: 13, color: T.textDim }}>V.I.K.I. sta studiando il tuo profilo…</div>}
      {state === "error" && (
        <div style={{ fontSize: 13, color: T.rose }}>
          Non sono riuscita a generare la strategia. <button onClick={generate} style={{ background: "none", border: "none", color: T.viki, cursor: "pointer", padding: 0, fontSize: 13 }}>Riprova</button>
        </div>
      )}
      {state === "done" && (
        <>
          <div data-testid="viki-strategy-text" style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 14, whiteSpace: "pre-wrap" }}>{strategy}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => navigator.clipboard.writeText(strategy)} style={{ fontSize: 12, background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 5, padding: "5px 10px", cursor: "pointer" }}>Copia</button>
            <button onClick={generate} style={{ fontSize: 12, background: "none", border: "none", color: T.textDim, cursor: "pointer", padding: "5px 4px" }}>Genera di nuovo</button>
          </div>
        </>
      )}
    </div>
  );
}

export function QuickStartWizard({ profile, staticData, onApplyCampaign, onConnectInstagram, onClose }) {
  const [step, setStep] = useState(0);
  const [strategyState, setStrategyState] = useState("idle");
  const [strategy, setStrategy] = useState("");
  const [campaignApplied, setCampaignApplied] = useState(false);

  const recommendedCampaign = staticData.campaigns.find((c) => c.id === staticData.recommendedCampaignByGoal[profile.goal]) || staticData.campaigns[0];
  const steps = ["Il tuo profilo Instagram", "La tua strategia", "Il tuo calendario", "Collega Instagram", "Pronto"];

  const runStrategy = useCallback(async () => {
    setStrategyState("loading");
    try {
      const { data } = await api.post(`/profiles/${profile.id}/strategy`);
      setStrategy(data.strategy);
      setStrategyState("done");
    } catch (e) {
      setStrategyState("error");
    }
  }, [profile.id]);

  useEffect(() => {
    if (step === 1 && strategyState === "idle") runStrategy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function next() { setStep((s) => Math.min(Math.floor(s) + 1, steps.length - 1)); }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(30,30,51,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div className="modal-card" data-testid="quickstart-wizard" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 14, padding: 28, width: 520, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 50px rgba(30,30,60,0.3)" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
          {steps.map((_, i) => <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? T.amber : T.line }} />)}
        </div>

        {step === 0 && (
          <div>
            <div style={{ fontFamily: T.fontHead, fontSize: 18, marginBottom: 6 }}>Hai già un profilo Instagram?</div>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 18 }}>Serve per pubblicare quello che prepariamo insieme nei prossimi passaggi.</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
              <button data-testid="quickstart-has-instagram-btn" onClick={next} style={{ ...primaryBtn, flex: 1 }}>Sì, ce l'ho già</button>
              <button data-testid="quickstart-no-instagram-btn" onClick={() => setStep(0.5)} style={{ flex: 1, background: "transparent", border: `1px solid ${T.line}`, color: T.text, borderRadius: 7, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>No, guidami tu</button>
            </div>
          </div>
        )}

        {step === 0.5 && (
          <div>
            <div style={{ fontFamily: T.fontHead, fontSize: 18, marginBottom: 14 }}>Crea il tuo profilo Instagram</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
              {staticData.instagramChecklist.map((item, i) => (
                <div key={i} style={{ fontSize: 13, display: "flex", gap: 10, lineHeight: 1.5 }}>
                  <span style={{ color: T.amber, fontFamily: T.fontHead, flexShrink: 0 }}>{i + 1}.</span>{item}
                </div>
              ))}
            </div>
            <button data-testid="quickstart-checklist-done-btn" onClick={() => setStep(1)} style={{ ...primaryBtn, width: "100%" }}>Fatto, continua</button>
          </div>
        )}

        {step === 1 && (
          <div>
            <div style={{ fontFamily: T.fontHead, fontSize: 18, marginBottom: 6 }}>La tua strategia</div>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 16 }}>V.I.K.I. sta scrivendo una strategia su misura per {profile.name}.</div>
            {strategyState === "loading" && <div style={{ fontSize: 13, color: T.textDim, marginBottom: 18 }}>Un momento…</div>}
            {strategyState === "error" && <div style={{ fontSize: 13, color: T.rose, marginBottom: 18 }}>Non sono riuscita a generarla, ma puoi comunque continuare e riprovare più tardi da "Il mio piano".</div>}
            {strategyState === "done" && <div data-testid="quickstart-strategy-text" style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 18, background: T.surfaceAlt, border: `1px solid ${T.line}`, borderRadius: 8, padding: 14, whiteSpace: "pre-wrap" }}>{strategy}</div>}
            <button data-testid="quickstart-strategy-continue-btn" onClick={next} style={{ ...primaryBtn, width: "100%" }}>Continua</button>
          </div>
        )}

        {step === 2 && (
          <div>
            <div style={{ fontFamily: T.fontHead, fontSize: 18, marginBottom: 6 }}>Il tuo calendario</div>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 14 }}>Per il tuo obiettivo ("{profile.goal}") questa è la campagna più adatta per iniziare:</div>
            <div style={{ background: T.surfaceAlt, border: `1px solid ${T.line}`, borderRadius: 8, padding: 14, marginBottom: 18 }}>
              <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 6 }}>{recommendedCampaign.title}</div>
              <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.5 }}>{recommendedCampaign.rationale}</div>
            </div>
            {campaignApplied ? (
              <div style={{ fontSize: 13, color: T.teal, marginBottom: 14 }}>Campagna applicata al calendario.</div>
            ) : (
              <button data-testid="quickstart-apply-campaign-btn" onClick={async () => { await onApplyCampaign(recommendedCampaign.id); setCampaignApplied(true); }} style={{ ...primaryBtn, width: "100%", marginBottom: 10 }}>Applica al calendario</button>
            )}
            <button data-testid="quickstart-calendar-continue-btn" onClick={next} style={{ width: "100%", background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 7, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>{campaignApplied ? "Continua" : "Salta questo passaggio"}</button>
          </div>
        )}

        {step === 3 && (
          <div>
            <div style={{ fontFamily: T.fontHead, fontSize: 18, marginBottom: 6 }}>Collega Instagram</div>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 18 }}>Serve per programmare la pubblicazione direttamente dal calendario, invece di pubblicare a mano. Collega anche la tua Pagina Facebook, se ne hai una collegata a Instagram.</div>
            {profile.instagram_connected ? (
              <div style={{ fontSize: 13, color: T.teal, marginBottom: 18 }}>Instagram è già collegato.</div>
            ) : (
              <button data-testid="quickstart-connect-instagram-btn" onClick={async () => { await onConnectInstagram(); next(); }} style={{ ...primaryBtn, width: "100%", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <Instagram size={15} /> Collega Instagram
              </button>
            )}
            <button data-testid="quickstart-instagram-skip-btn" onClick={next} style={{ width: "100%", background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 7, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>{profile.instagram_connected ? "Continua" : "Salta per ora"}</button>
          </div>
        )}

        {step === 4 && (
          <div>
            <div style={{ fontFamily: T.fontHead, fontSize: 18, marginBottom: 10 }}>Tutto pronto</div>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 18, lineHeight: 1.6 }}>
              Hai una strategia, un calendario con le prime idee pronte{profile.instagram_connected ? " e Instagram collegato" : ""}.
              Da qui in poi trovi tutto nella schermata "Oggi": un'azione alla volta.
            </div>
            <button data-testid="quickstart-finish-btn" onClick={onClose} style={{ ...primaryBtn, width: "100%" }}>Vai alla Home</button>
          </div>
        )}
      </div>
    </div>
  );
}
